import { AsyncPipe } from '@angular/common';
import { Component, OnInit, SecurityContext, inject } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  Cipher,
  CipherAlgorithm,
  CipherMode,
  CipherPadding,
  CryptoModule,
  Encoding,
  EncodingService,
  EncryptionService,
} from '@fmnts/common/crypto';
import { logger } from '@fmnts/common/log';
import { isRecord } from '@fmnts/core';
import {
  AbstractDeclarativeFieldComponent,
  FormTextboxField,
  TemplateBuilder,
  interpolateTemplate,
} from '@fmnts/declarative-forms';
import { I18nModule, TranslationService } from '@fmnts/i18n';
import * as Fn from 'effect/Function';
import * as O from 'effect/Option';
import { EMPTY, Observable, catchError, map, of } from 'rxjs';

const urlPrefixToSubstitue = 'https://unicef.agenciaproa.com';

/**
 * Component that renders a text box.
 */
@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'next-text-box-field',
  templateUrl: './text-box-field.component.html',
  imports: [CryptoModule, I18nModule, AsyncPipe],
})
export class TextBoxFieldComponent
  extends AbstractDeclarativeFieldComponent<FormTextboxField>
  implements OnInit
{
  private readonly sanitizer = inject(DomSanitizer);
  private readonly translationService = inject(TranslationService);
  private readonly encodingService = inject(EncodingService);
  private readonly encryptionService = inject(EncryptionService);
  private readonly templateBuilder = inject(TemplateBuilder);
  private readonly log = logger({ name: 'TextBoxFieldComponent' });

  /**
   * Observable that emits with the HTML content
   * that should be shown
   */
  protected htmlContent$: Observable<string> | undefined;
  /**
   * Observable that emits with the image src.
   * Emits `undefined` if there is no image source
   * for this field
   */
  protected imageSrc$: Observable<string | undefined> | undefined;
  private _cipher: Cipher | undefined;

  private get cipher(): Cipher {
    if (!this._cipher) {
      // If not created yet, set up cipher
      const secret = this.encryptionService.createKey(
        'changethis123456',
        Encoding.Hex,
      );

      this._cipher = this.encryptionService.createCipher({
        algorithm: CipherAlgorithm.AES,
        defaultOptions: {
          secret,
          mode: CipherMode.ECB,
          padding: CipherPadding.ZeroPadding,
        },
      });
    }

    return this._cipher;
  }

  // TODO(i18n): Output of empty translations
  protected get hasLabel(): boolean {
    const label = this.field?.label ?? '';
    return isRecord(label) ? Object.keys(label).length > 0 : !!label;
  }

  ngOnInit(): void {
    this.imageSrc$ = this.imageSrcFromField$(this.field);

    this.htmlContent$ = this.htmlContentFromField$(this.field).pipe(
      map((unsanitized) => {
        const sanitized = Fn.pipe(
          this.sanitizeHtml(unsanitized),
          O.getOrElse(() => ''),
        );

        const hasDynamicLink =
          sanitized.indexOf(`href="${urlPrefixToSubstitue}`) !== -1;

        return hasDynamicLink
          ? this.replaceWithSignedLink(sanitized)
          : sanitized;
      }),
    );
  }

  private imageSrcFromField$(field: FormTextboxField) {
    const { image } = field.widgetOptions;
    if (
      !image ||
      (typeof image === 'object' && Object.keys(image).length === 0)
    ) {
      return EMPTY;
    }

    return this.translationService.fromInlineTranslation$(of(image)).pipe(
      map((imageSrc) => new URL(imageSrc).toString()),
      catchError(() => of(undefined)),
    );
  }

  private htmlContentFromField$(field: FormTextboxField) {
    // observable that emits with the translated text
    const text$ = this.translationService.fromInlineTranslation$(
      of(field.widgetOptions.text),
    );

    return text$.pipe(
      interpolateTemplate((part) =>
        this.templateBuilder
          .interpolate(part)
          // Catch any error and render an empty string
          .pipe(catchError((e) => of(''))),
      ),
    );
  }

  private replaceWithSignedLink(text: string) {
    const regexpResult = /href="([^"]*)"/.exec(text);

    const fullAttribute = regexpResult?.[0];
    const link = regexpResult?.[1];
    const newLink = this.getLink(link ?? '');
    if (!fullAttribute || !link) {
      return text;
    }
    return text.replace(fullAttribute, `href="${newLink}"`);
  }

  private getLink(link: string): string {
    const url = new URL(link);

    const signature = this.signSignature(url.searchParams.toString());
    const searchParams = new URLSearchParams({
      signature,
    });

    url.search = searchParams.toString();

    return url.toString();
  }

  private signSignature(message: string): string {
    const base64Message = this.encodingService.encode({
      message,
      input: Encoding.Utf8,
      output: Encoding.Base64,
    });

    return this.cipher.encrypt(base64Message);
  }

  /**
   * Sanitize html
   *
   * Simply just strip away any potential malicious string, and return
   * the modified string
   */
  private sanitizeHtml(unsanitized: string): O.Option<string> {
    const sanitized = Fn.pipe(
      this.sanitizer.sanitize(SecurityContext.HTML, unsanitized),
      O.fromNullable,
    );

    if (unsanitized !== O.getOrUndefined(sanitized)) {
      this.log.info('Sanitizing content for field: ', this.field.key);
    }

    return sanitized;
  }
}
